
import React, { useState } from 'react';
import { 
  ChevronRight, Wand2, BookOpen, PenTool, Sparkles, 
  Brain, Music, ShieldCheck, Heart, Volume2, CheckCircle 
} from './Icons';

interface HeroProps {
  onStart: (initialPrompt?: string) => void;
}

const Hero: React.FC<HeroProps> = ({ onStart }) => {
  const [prompt, setPrompt] = useState('');

  const handleStart = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    onStart(prompt);
  };

  return (
    <div className="flex flex-col items-center px-4 relative overflow-hidden">
      {/* Hero Section */}
      <section className="min-h-[90vh] flex flex-col items-center justify-center text-center w-full relative" aria-label="Introduction">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[900px] bg-spark-600 rounded-full blur-[180px] opacity-20 -z-10 mix-blend-screen animate-pulse pointer-events-none"></div>
        <div className="absolute bottom-0 right-0 w-[700px] h-[700px] bg-forge-600 rounded-full blur-[150px] opacity-15 -z-10 animate-float pointer-events-none" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-gold-500 rounded-full blur-[120px] opacity-15 -z-10 animate-float pointer-events-none" style={{ animationDelay: '4s' }}></div>

        <div className="max-w-5xl mx-auto space-y-10 relative z-10 pt-16">
          <div 
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/5 border border-white/10 text-[10px] font-black text-white uppercase tracking-[0.3em] backdrop-blur-xl shadow-2xl animate-slide-up"
            style={{ animationFillMode: 'both' }}
          >
            <Sparkles className="w-4 h-4 text-yellow-300" />
            <span>Premium AI Personalized Stories</span>
          </div>

          <h1 
            className="text-5xl md:text-8xl font-sans font-black leading-[0.95] drop-shadow-2xl animate-slide-up tracking-tighter"
            style={{ animationDelay: '0.1s', animationFillMode: 'both' }}
          >
            Create stories as <br />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-spark-400 via-forge-400 to-gold-400 italic font-serif">
              Unique as They Are
            </span>
          </h1>

          <p 
            className="text-xl md:text-2xl text-gray-300/80 max-w-3xl mx-auto font-light leading-relaxed animate-slide-up px-4"
            style={{ animationDelay: '0.2s', animationFillMode: 'both' }}
          >
            The ultimate AI kids story generator. Turn a spark of imagination into a beautifully illustrated, narrated book where your child is the star.
          </p>

          <div 
            className="w-full max-w-3xl mx-auto mt-12 animate-slide-up px-4"
            style={{ animationDelay: '0.3s', animationFillMode: 'both' }}
          >
              <form onSubmit={handleStart} className="relative group">
                  <div className="absolute -inset-1.5 bg-gradient-to-r from-spark-500 via-forge-500 to-gold-500 rounded-[2.5rem] blur-xl opacity-20 group-focus-within:opacity-40 transition duration-1000"></div>
                  <div className="relative flex flex-col md:flex-row items-stretch md:items-center bg-[#1a1825]/80 backdrop-blur-2xl border border-white/10 rounded-[2rem] p-3 shadow-3xl">
                      <textarea
                          value={prompt}
                          onChange={(e) => setPrompt(e.target.value)}
                          placeholder="What's the next adventure? (e.g. Maya the explorer finds a talking dragon)"
                          className="flex-1 bg-transparent border-none text-white placeholder-gray-500 text-xl px-6 py-4 focus:ring-0 focus:outline-none resize-none h-24 md:h-20 flex items-center pt-5"
                          aria-label="Story theme or prompt"
                          onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  handleStart();
                              }
                          }}
                      />
                      <button 
                          type="submit"
                          className="bg-white text-black hover:bg-gold-50 transition-all rounded-2xl px-10 py-4 font-black text-xl flex items-center justify-center gap-3 h-16 md:h-20 md:mr-1 mt-3 md:mt-0 shadow-2xl active:scale-95"
                      >
                          Create
                          <Wand2 className="w-6 h-6" />
                      </button>
                  </div>
              </form>
          </div>

          <div 
            className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-20 md:pt-32 pb-20 text-left animate-slide-up px-4"
            style={{ animationDelay: '0.5s', animationFillMode: 'both' }}
          >
            <div className="glass-panel p-8 rounded-[2rem] space-y-5 border border-white/5 hover:border-spark-500/20 transition-all duration-500">
              <div className="w-14 h-14 bg-spark-500/20 rounded-2xl flex items-center justify-center text-spark-400">
                <span className="font-black text-2xl" aria-hidden="true">01</span>
              </div>
              <h2 className="text-2xl font-bold text-white tracking-tight">Describe It</h2>
              <p className="text-gray-400 text-base leading-relaxed">Simply type an idea. Our AI weaves a unique, age-appropriate plot centered around your child.</p>
            </div>
            
            <div className="glass-panel p-8 rounded-[2rem] space-y-5 border border-white/5 hover:border-forge-500/20 transition-all duration-500">
              <div className="w-14 h-14 bg-forge-500/20 rounded-2xl flex items-center justify-center text-forge-400">
                 <span className="font-black text-2xl" aria-hidden="true">02</span>
              </div>
              <h2 className="text-2xl font-bold text-white tracking-tight">Personalize</h2>
              <p className="text-gray-400 text-base leading-relaxed">Upload a photo to ensure characters look like them, and pick from world-class art styles.</p>
            </div>
            
            <div className="glass-panel p-8 rounded-[2rem] space-y-5 border border-white/5 hover:border-gold-500/20 transition-all duration-500">
              <div className="w-14 h-14 bg-gold-500/20 rounded-2xl flex items-center justify-center text-gold-400">
                 <span className="font-black text-2xl" aria-hidden="true">03</span>
              </div>
              <h2 className="text-2xl font-bold text-white tracking-tight">Read & Share</h2>
              <p className="text-gray-400 text-base leading-relaxed">Enjoy the story with full narration. Download high-quality PDFs to print or share with family.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Narrative Engine Feature */}
      <section className="w-full max-w-7xl mx-auto py-24 md:py-40 space-y-20 md:space-y-40 px-6" aria-labelledby="features-heading">
        <div className="text-center space-y-8 max-w-5xl mx-auto animate-slide-up">
            <h2 id="features-heading" className="text-4xl md:text-7xl font-serif font-bold text-white leading-tight">
                Magical Stories <br />
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-gold-400 to-forge-400 italic">Built for Families</span>
            </h2>
            <p className="text-xl md:text-2xl text-gray-400 leading-relaxed font-light">
                StoryForge isn't just generating text. We use a proprietary <span className="text-gold-400 font-bold">Narrative Engine</span> to ensure every story follows a classic arc with meaningful lessons.
            </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
            <div className="glass-panel p-10 md:p-16 rounded-[3rem] space-y-8 border-white/10 relative group hover:border-gold-500/40 transition-all duration-700 overflow-hidden">
                <div className="absolute top-0 right-0 w-48 h-48 bg-gold-500/10 rounded-full blur-[100px] group-hover:bg-gold-500/20 transition-all"></div>
                <div className="w-20 h-20 bg-gold-500/20 rounded-3xl flex items-center justify-center text-gold-400 shadow-glow mb-10">
                    <Brain className="w-10 h-10" aria-hidden="true" />
                </div>
                <h3 className="text-3xl md:text-5xl font-serif font-bold text-white leading-tight">Advanced Story <br/>Architecture</h3>
                <p className="text-xl text-gray-400 leading-relaxed">
                    We plan the entire story arc before writing a single word, ensuring:
                </p>
                <ul className="space-y-4 text-lg">
                    <li className="flex items-center gap-4 text-gold-200/90"><CheckCircle className="w-6 h-6 text-gold-500" /> Age-appropriate vocabulary</li>
                    <li className="flex items-center gap-4 text-gold-200/90"><CheckCircle className="w-6 h-6 text-gold-500" /> Coherent character growth</li>
                    <li className="flex items-center gap-4 text-gold-200/90"><CheckCircle className="w-6 h-6 text-gold-500" /> Emotional resonance & heart</li>
                </ul>
            </div>

            <div className="glass-panel p-10 md:p-16 rounded-[3rem] space-y-8 border-white/10 relative group hover:border-spark-500/40 transition-all duration-700 overflow-hidden">
                <div className="absolute top-0 right-0 w-48 h-48 bg-spark-500/10 rounded-full blur-[100px] group-hover:bg-spark-500/20 transition-all"></div>
                <div className="w-20 h-20 bg-spark-500/20 rounded-3xl flex items-center justify-center text-spark-400 shadow-glow mb-10">
                    <BookOpen className="w-10 h-10" aria-hidden="true" />
                </div>
                <h3 className="text-3xl md:text-5xl font-serif font-bold text-white leading-tight">World-Class Art <br/>Illustration</h3>
                <p className="text-xl text-gray-400 leading-relaxed">
                    Each page features custom art that matches your selected style perfectly:
                </p>
                <ul className="space-y-4 text-lg">
                    <li className="flex items-center gap-4 text-spark-200/90"><CheckCircle className="w-6 h-6 text-spark-500" /> Consistent character appearance</li>
                    <li className="flex items-center gap-4 text-spark-200/90"><CheckCircle className="w-6 h-6 text-spark-500" /> Vibrant, high-resolution imagery</li>
                    <li className="flex items-center gap-4 text-spark-200/90"><CheckCircle className="w-6 h-6 text-spark-500" /> Professional-grade composition</li>
                </ul>
            </div>
        </div>

        <div className="relative group rounded-[4rem] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-forge-600/40 via-gold-600/40 to-spark-600/40 opacity-50 group-hover:opacity-70 transition-opacity duration-1000"></div>
          <div className="relative bg-[#1a1825]/60 backdrop-blur-3xl p-12 md:p-24 border border-white/10 text-center flex flex-col items-center">
              <h2 className="text-3xl md:text-6xl font-serif font-bold text-white mb-8 leading-tight">Ready to forge a memory?</h2>
              <p className="text-xl md:text-2xl text-white/70 leading-relaxed font-light mb-14 max-w-3xl">
                  Join 10,000+ parents who are transforming bedtime into a magical experience with personalized AI bedtime stories.
              </p>
              <button 
                  onClick={() => handleStart()}
                  className="px-16 py-7 bg-white text-black font-black text-2xl rounded-3xl shadow-3xl hover:bg-gold-50 hover:scale-[1.02] active:scale-[0.98] transition-all w-full md:w-auto"
              >
                  Forge Your First Story
              </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Hero;
